// A discreet «Έλεγχος παραδοχών» button and the read-only window it opens.
// It reads the project settings and lists what looks unusual; it changes
// nothing and never blocks the run (see config/assumptionChecks.js).
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { getBatteryDetails } from "../services/api";
import { assumptionChecks, studyBattery } from "./config/assumptionChecks";

export default function AssumptionCheck({ cfg, isGreek = true }) {
  const t = (el, en) => (isGreek ? el : en);
  const [open, setOpen] = useState(false);
  const [unitPrice, setUnitPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const battery = useMemo(() => studyBattery(cfg), [cfg]);

  // The catalog price is fetched only when the window opens and only when no
  // override replaces it - the button itself costs nothing on the page.
  useEffect(() => {
    if (!open || !battery || battery.priceOverrideEur != null) return undefined;
    let cancelled = false;
    setLoading(true);
    getBatteryDetails(battery.name)
      .then((det) => {
        const raw = Number(det?.characteristics?.Price);
        if (!cancelled) setUnitPrice(Number.isFinite(raw) ? raw : null);
      })
      .catch(() => { if (!cancelled) setUnitPrice(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, battery]);

  const batteryPriceEur = battery && unitPrice != null ? unitPrice * battery.count : null;
  const { findings, checked } = useMemo(
    () => assumptionChecks(cfg, { batteryPriceEur, isGreek }),
    [cfg, batteryPriceEur, isGreek]
  );

  return (
    <>
      <Button
        variant="text"
        size="small"
        color="inherit"
        startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => setOpen(true)}
        data-testid="assumption-check-button"
        data-no-translate=""
        sx={{ ml: 1.5, color: "text.secondary", textTransform: "none", fontWeight: 600,
          "&:hover": { color: "primary.main", background: "transparent" } }}
      >
        {t("Έλεγχος παραδοχών", "Check assumptions")}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle data-no-translate="">{t("Έλεγχος παραδοχών", "Assumption check")}</DialogTitle>
        <DialogContent dividers data-no-translate="" data-testid="assumption-check-dialog">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "Μόνο για ανάγνωση. Ελέγχονται οι ρυθμίσεις του έργου για τιμές που συχνά είναι λάθος ή που κρίνουν το αποτέλεσμα. Δεν αλλάζει τίποτα και δεν εμποδίζει την εκτέλεση της μελέτης.",
              "Read-only. The project's settings are checked for values that are often wrong or that decide the result. Nothing is changed and the study can run as it is."
            )}
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
          ) : findings.length ? (
            findings.map((item) => (
              <Alert key={item.id} severity={item.severity} sx={{ mb: 1.5 }} data-testid={`assumption-${item.id}`}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{item.why}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <b>{t("Πού: ", "Where: ")}</b>{item.where}
                </Typography>
              </Alert>
            ))
          ) : (
            <Alert severity="success" data-testid="assumption-check-clear">
              {t("Δεν βρέθηκε κάτι ασυνήθιστο σε όσα ελέγχονται.", "Nothing unusual was found in what is checked.")}
            </Alert>
          )}
          {checked.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {t("Ελέγχθηκαν: ", "Checked: ")}{checked.join(", ")}.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} data-no-translate="">{t("Κλείσιμο", "Close")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
